/**
 * RetryPolicy manages retry logic with exponential backoff.
 */
export class RetryPolicy {
  maxAttempts: number;

  initialDelay: number;

  backoffMultiplier: number;

  maxDelay: number;

  currentAttempt: number = 0;

  isCancelled: boolean = false;

  constructor(options: {
    maxAttempts?: number;
    initialDelay?: number;
    backoffMultiplier?: number;
    maxDelay?: number;
  } = {}) {
    this.maxAttempts = options.maxAttempts ?? 15;
    this.initialDelay = options.initialDelay ?? 2000;
    this.backoffMultiplier = options.backoffMultiplier ?? 1.5;
    this.maxDelay = options.maxDelay ?? 60000;
  }

  /**
   * Calculate the delay in milliseconds for the next retry attempt.
   */
  getNextDelay(): number {
    const delay = this.initialDelay * this.backoffMultiplier ** this.currentAttempt;
    return Math.min(delay, this.maxDelay);
  }

  /**
   * Check if we should retry (have attempts remaining).
   */
  shouldRetry(): boolean {
    return this.currentAttempt < this.maxAttempts;
  }

  /**
   * Get the current attempt number (1-indexed).
   */
  getAttemptNumber(): number {
    return this.currentAttempt + 1;
  }

  /**
   * Get the maximum number of attempts.
   */
  getMaxAttempts(): number {
    return this.maxAttempts;
  }

  /**
   * Increment attempt counter and return whether we can retry.
   */
  incrementAttempt(): boolean {
    this.currentAttempt++;
    return this.shouldRetry();
  }

  /**
   * Cancel any pending retries.
   */
  cancel(): void {
    this.isCancelled = true;
  }

  /**
   * Check if retries have been cancelled.
   */
  wasCancelled(): boolean {
    return this.isCancelled;
  }

  /**
   * Reset the policy to initial state (for reusing).
   */
  reset(): void {
    this.currentAttempt = 0;
    this.isCancelled = false;
  }

  /**
   * Sleep for the calculated delay before next retry.
   * Returns a promise that resolves when delay is complete or rejects if cancelled.
   */
  async waitForNextRetry(): Promise<void> {
    if (this.isCancelled) {
      throw new Error('Retry policy was cancelled');
    }
    const delay = this.getNextDelay();
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        if (this.isCancelled) {
          reject(new Error('Retry policy was cancelled'));
        } else {
          resolve();
        }
      }, delay);

      // Store timeout for potential cancellation
      (this as any)._timeoutId = timeout;
    });
  }

  /**
   * Cancel any pending waitForNextRetry promise.
   */
  cancelWait(): void {
    const timeoutId = (this as any)._timeoutId;
    if (timeoutId) {
      clearTimeout(timeoutId);
      delete (this as any)._timeoutId;
    }
  }
}

/**
 * Determine if an error is likely a network/connectivity error.
 */
export function isNetworkError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }

  const message = error.message.toLowerCase();
  const networkErrors = ['network', 'timeout', 'refused', 'unreachable', 'econnrefused', 'econnreset', 'etimedout', 'enotfound', 'dns', 'getaddrinfo', 'socket hang up'];

  return networkErrors.some((err) => message.includes(err));
}

/**
 * Extract a user-friendly error message from an error object.
 */
export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return 'Unknown error occurred';
}
